import QueryString from 'qs'

import { AiPlatformAPI } from 'api'

export interface Scenario {
  scenarioId: string
  name: string
  prompt: string
  exit?: string
  exitQuestion?: string
  useExit?: boolean
  instruction?: string
  useInstruction?: boolean
  editable: boolean
  archived: boolean
  driftManaged: boolean
  scenarioExamples: string[]
  templateId: string
}

interface ScenariosTableResponse {
  scenariosPageRows: Scenario[]
  meta: {
    next: number
    total: number
    totalPages: number
  }
}

export interface ScenariosTableQueryParams {
  nameQuery?: string
  sortProperty?: string
  sortDirection?: 'desc' | 'asc'
  status?: 'ENABLED' | 'ARCHIVED' | 'DELETED'
  archived?: boolean
  page?: number
  pageSize?: 5 | 10 | 25 | 50
  orgId: string
}

export interface ReducedScenarioEntity {
  scenarioId: string // Assuming UUID is represented as a string
  name: string
  exposed: boolean
  type: string
  archived: boolean
}

export const getScenariosTableData = (params: ScenariosTableQueryParams) => {
  const query = QueryString.stringify(params, { arrayFormat: 'repeat' })
  return AiPlatformAPI.get<ScenariosTableResponse>(
    `api/v1/scenarios/org/${params.orgId}/?${query}`
  )
}

export interface PromptCreationSteps {
  stepType: string
  question: string
  answer: string
  nextStepType: string
}

export interface PromptConfig {
  agentType: string
  creationSteps: PromptCreationSteps[]
  followUpQuestion: string
  answer?: string
  currentStepId: string
  validatedPrompt?: string | null
  isFinished: boolean
}

export interface InitialPromptConfig {
  agentType: string
}

export const createScenario = ({
  orgId,
  payload,
}: {
  orgId: string
  payload: Partial<Scenario>
}) => AiPlatformAPI.post<string>(`/api/v1/scenarios/org/${orgId}/`, payload)

export const updateScenario = ({
  orgId,
  payload,
}: {
  orgId: string
  payload: Partial<Scenario>
}) => AiPlatformAPI.patch<string>(`/api/v1/scenarios/org/${orgId}`, payload)

export const getScenario = ({
  scenarioId,
  orgId,
}: {
  scenarioId: string
  orgId: string
}) =>
  AiPlatformAPI.get<Scenario>(`/api/v1/scenarios/org/${orgId}/${scenarioId}`)

export const archiveScenario = ({
  scenarioId,
  orgId,
}: {
  scenarioId: string
  orgId: string
}) => AiPlatformAPI.get(`/api/v1/scenarios/org/${orgId}/${scenarioId}/archive`)

export const gerReducedScenarios = () =>
  AiPlatformAPI.get(`/api/v1/scenarios/org/review`)

export const restoreScenario = ({
  scenarioId,
  orgId,
}: {
  scenarioId: string
  orgId: string
}) => AiPlatformAPI.get(`/api/v1/scenarios/org/${orgId}/${scenarioId}/restore`)

export const deleteScenario = ({
  scenarioId,
  orgId,
}: {
  scenarioId: string
  orgId: string
}) => AiPlatformAPI.delete(`/api/v1/scenarios/org/${orgId}/${scenarioId}`)

export const validateDescription = (description: string) =>
  AiPlatformAPI.post('/api/v1/scenarios/validation/description/', description)

export const validateInstructions = (instructions: string) =>
  AiPlatformAPI.post('/api/v1/scenarios/validation/instructions/', instructions)

export const validateExit = (exit: string) =>
  AiPlatformAPI.post('/api/v1/scenarios/validation/exit/', exit)

export const getActiveCustomScenariosCount = ({ orgId }: { orgId: string }) =>
  AiPlatformAPI.get(`/api/v1/scenarios/org/${orgId}/activeCustomScenariosCount`)

export const postPromptToConfig = (
  config: PromptConfig | InitialPromptConfig
) => AiPlatformAPI.post(`/api/agents/to_config`, config)

export const getScenarioTrainingData = ({ orgId }: { orgId: string }) =>
  AiPlatformAPI.get(`/api/v1/scenarios/org/${orgId}/isTraining`)
